import { MessageCircleQuestion, Sparkles, Telescope, Workflow } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, StarterCard, StarterCards, ToggleGroup, ToggleGroupItem, cn } from "@ncp/ui";
import { PlatformIcon } from "../PlatformIcon";
import type { PlaybookOption } from "./Composer";

export type StartMode = "ask" | "research" | "process";
export interface StarterPrompt { text: string; icon?: string | null }

const RESEARCH_PREFIX = /^\s*(deep research|research in depth|بحث معم[ّ]?ق)\s*[:：-]\s*/i;

/** Splits an assistant's suggested prompts into plain questions and research questions ("Deep research: …", with the label removed). */
export function splitPrompts(prompts: StarterPrompt[]): { ask: StarterPrompt[]; research: StarterPrompt[] } {
  const ask: StarterPrompt[] = [];
  const research: StarterPrompt[] = [];
  for (const p of prompts) {
    if (RESEARCH_PREFIX.test(p.text)) research.push({ ...p, text: p.text.replace(RESEARCH_PREFIX, "") });
    else ask.push(p);
  }
  return { ask, research };
}

/**
 * The start of a conversation: three ways in (ask a question, research in depth, start a process), each with cards to begin from.
 * Picking a question sends it in the chosen mode; picking a process opens it. Without `modes` there is no choice to make: the cards
 * are the questions, then the research questions, and each one is sent the way it was written.
 */
export function ChatEmptyState({ greeting, hint, mode, onModeChange, prompts, playbooks, onPrompt, onStartProcess, compact = false, modes = true, max, className }: {
  greeting?: string | null;
  hint?: string | null;
  mode: StartMode;
  onModeChange: (mode: StartMode) => void;
  prompts: StarterPrompt[];
  playbooks: PlaybookOption[];
  onPrompt: (text: string, mode: Exclude<StartMode, "process">) => void;
  onStartProcess?: (playbookKey: string) => void;
  compact?: boolean;
  /** False hides the Ask / Research / Process choice (a page's copilot, where the page offers the starters). */
  modes?: boolean;
  /** How many starter cards to show; by default 2 when compact, else 4. */
  max?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const { ask, research } = splitPrompts(prompts);
  const canProcess = !!onStartProcess && playbooks.length > 0;
  const shown = !modes ? "ask" : mode === "process" && !canProcess ? "ask" : mode;
  const limit = max ?? (compact ? 2 : 4);
  const examples = !modes
    ? [...ask.map((p) => ({ ...p, as: "ask" as const })), ...research.map((p) => ({ ...p, as: "research" as const }))]
    : (shown === "research" ? (research.length > 0 ? research : ask) : ask).map((p) => ({ ...p, as: shown === "research" ? "research" as const : "ask" as const }));
  const modeHint = shown === "research" ? t("ai.chat.modes.researchHint") : shown === "process" ? t("ai.chat.modes.processHint") : t("ai.chat.modes.askHint");
  return (
    <div className={cn("mx-auto flex w-full max-w-2xl flex-col items-center text-center", compact ? "gap-3 py-4" : "gap-5 py-10", className)}>
      <span data-slot="assistant-empty-icon" className={cn("grid place-items-center rounded-2xl bg-gradient-to-br from-aegold-100 via-white to-seablue-100 text-aegold-800 shadow-sand ring-1 ring-aegold-200/70", compact ? "size-10" : "size-14")}>
        <Sparkles className={compact ? "size-5" : "size-7"} />
      </span>
      <div className="max-w-xl">
        <h2 className={cn("font-semibold text-aeblack-950 text-balance", compact ? "text-base" : "text-xl")}>{greeting || t("ai.chat.emptyTitle")}</h2>
        {!compact ? <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{hint || t("ai.chat.emptyHint")}</p> : null}
      </div>
      {modes ? (
        <ToggleGroup type="single" value={shown} onValueChange={(v) => { if (v) onModeChange(v as StartMode); }} aria-label={t("ai.chat.modes.label")} className="flex-wrap justify-center" size={compact ? "sm" : "default"}>
          <ToggleGroupItem value="ask"><MessageCircleQuestion /> {t("ai.chat.modes.ask")}</ToggleGroupItem>
          <ToggleGroupItem value="research"><Telescope /> {t("ai.chat.modes.research")}</ToggleGroupItem>
          {canProcess ? <ToggleGroupItem value="process"><Workflow /> {t("ai.chat.modes.process")}</ToggleGroupItem> : null}
        </ToggleGroup>
      ) : null}
      {modes && !compact ? <p className="-mt-2 max-w-lg text-xs leading-relaxed text-muted-foreground">{modeHint}</p> : null}
      <StarterCards className={cn("w-full", compact && "sm:grid-cols-1")}>
        {shown === "process"
          ? playbooks.map((p) => (
              <StarterCard key={p.key} icon={<Workflow />} title={p.label} description={p.description} meta={<Badge size="sm" variant="gold">{t("ai.chat.processBadge")}</Badge>} onClick={() => onStartProcess?.(p.key)} />
            ))
          : examples.slice(0, limit).map((p) => (
              <StarterCard key={modes ? p.text : `${p.as}:${p.text}`} icon={<PlatformIcon name={p.icon} fallback={p.as === "research" ? Telescope : MessageCircleQuestion} />} title={<span className="font-medium">{p.text}</span>} onClick={() => onPrompt(p.text, p.as)} />
            ))}
      </StarterCards>
    </div>
  );
}
